import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from './toast-service';
import { SpinnerService } from './spinner.service';
import { ToastMessages } from '../shared/models/toast-messages';
import { TicketService } from './ticket.service';
import { Ticket, SpecialTicketData } from '../shared/models/ticket';
import { PaymentMethodType, PurchaseFormService } from './purchase-form.service';
import { RecaptchaService } from './recaptcha.service';
import { WebAPIClient } from './http-services/http-service';
import { TranslateService } from '@ngx-translate/core';
import { BackendError, ErrorService } from './error-service';

export interface PurchaseOrderResponse {
  orderId: string;
  orderNumber: string;
  totalPrice: number;
  currency: string;
  paymentRedirectUrl: string;
  paymentSessionId: string;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class PurchaseService {
  purchaseError$ = new BehaviorSubject<(BackendError & { heading: string; message: string }) | null>(null);

  constructor(
    private http: WebAPIClient,
    private toast: ToastService,
    private ticketService: TicketService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private formService: PurchaseFormService,
    private recaptchaService: RecaptchaService,
    private errorService: ErrorService
  ) { }

  async purchaseOrder(): Promise<void> {
    this.spinnerService.showPartialSpinner();
    this.purchaseError$.next(null);

    try {
      const [tickets, ticketData] = await firstValueFrom(
        combineLatest([this.formService.tickets$, this.ticketService.ticketData$])
      );

      const payload = this.prepareOrderPayload(tickets, ticketData);

      if (!payload) {
        this.toast.show('No tickets selected', 'warning');
        return;
      }

      await this.submitOrder(payload, ticketData?.id ?? '');

    } catch (error) {
      this.handleOrderError(error);
    } finally {
      this.spinnerService.hide();
    }
  }

  async resubmitOrder(sessionId: string): Promise<void> {
    this.spinnerService.showPartialSpinner();

    try {
      const redirectUrl = `https://checkout.reepay.com/#/${sessionId}`;
      window.location.href = redirectUrl;
    } catch (error) {
      this.handleOrderError(error);
    } finally {
      this.spinnerService.hide();
    }
  }

  createOrder(payload: any, captchaToken: string): Observable<PurchaseOrderResponse> {
    return this.http.post<PurchaseOrderResponse>('orders', payload, {
      headers: { 'X-Captcha-Token': captchaToken },
      withCredentials: true
    });
  }

  prepareOrderPayload(tickets: Ticket[], ticketData: SpecialTicketData | null) {
    const selectedTickets = tickets.filter(t => t.count > 0);

    if (selectedTickets.length === 0) return null;

    const contact = this.formService.getContactDetails();
    const dates = this.formService.getDateDetails();
    const payment = this.formService.getPaymentMethod();

    const passengers = selectedTickets.map(t => ({
      type: t.passengerType,
      count: t.count
    }));

    const paymentMethod = this.formService.isFreeOrder() ? PaymentMethodType.Free : payment;

    return {
      customer: {
        email: contact.type === 'email' ? contact.emailAddress : null,
        countryCode: contact.type === 'phone' ? contact.countryCode : '',
        phoneNumber: contact.type === 'phone' ? contact.phoneNumber : ''
      },
      order: {
        specialTicketProductId: ticketData?.id,
        passengers,
        validFrom: dates.startDate,
        validTo: dates.validToDate,
        language: this.translateService.getCurrentLang()
      },
      payment: {
        method: paymentMethod
      }
    };
  }

  private async submitOrder(payload: any, specialTicketProductId: string): Promise<void> {
    const captchaToken = await this.recaptchaService.execute('checkout');

    if (!captchaToken) {
      this.toast.show(ToastMessages.ERROR_PURCHASE_FAILED, 'error');
      setTimeout(() => this.toast.hide(), 4000);
      return;
    }

    const response = await firstValueFrom(this.createOrder(payload, captchaToken));

    sessionStorage.setItem('pendingOrderId', response.orderId);
    sessionStorage.setItem('pendingSpecialTicketProductId', specialTicketProductId);
    sessionStorage.setItem('pendingOrderPayload', JSON.stringify(payload));
    sessionStorage.setItem('pendingPaymentRedirectUrl', response.paymentRedirectUrl);

    this.formService.saveFormState();
    window.location.href = response.paymentRedirectUrl;
  }

  private handleOrderError(error: unknown): void {
    const backendError: BackendError = error instanceof HttpErrorResponse
      ? this.mapBackendError(error)
      : { Id: 'REJSEBILLET.WEB.GENERIC_ERROR', operationId: undefined };

    const messages = this.errorService.getErrorMessages(backendError.Id);

    this.purchaseError$.next({ ...backendError, ...messages });

    this.toast.show(messages.message, 'error');
    setTimeout(() => this.toast.hide(), 4000);
  }

  private mapBackendError(err: HttpErrorResponse): BackendError {
    const errorId = err.error?.code ?? err.error?.Id ?? 'REJSEBILLET.WEB.GENERIC_ERROR';
    const operationId = err.headers.get('X-Operation-ID') ?? undefined;

    const mappedId = this.errorService.hasErrorCode(errorId)
      ? errorId
      : 'REJSEBILLET.WEB.PURCHASE.ORDER_CREATION_FAILED';

    return { Id: mappedId, operationId };
  }
}
